import React, { useState, useEffect } from "react";
import { Modal, Form, Select, Input, message, Tag, Space, Divider } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { uldService } from "../../../../api/enhancedApi";
import { useUld } from "../context/UldContext";

const { Option } = Select;

const UldTagManageModal = ({ 
    visible, 
    onCancel, 
    onSuccess, 
    record 
}) => {
    const { tagList } = useUld();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    
    // 从正确的数据路径获取当前标签：record.master.tags
    const currentTags = record?.master?.tags || [];
    const currentTagNames = currentTags.map(tag => tag.tag_name);

    useEffect(() => {
        if (visible && record) {
            // 初始化表单
            form.setFieldsValue({
                tags_to_add: [],
                tags_to_remove: [],
                remarks: ""
            });
        }
    }, [visible, record, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // 只有在有实际操作时才调用API
            if (!values.tags_to_add?.length && !values.tags_to_remove?.length) {
                message.warning("请选择要添加或移除的标签");
                return;
            }

            console.log('🏷️ 标签管理开始:', {
                uldId: record.master.id,
                uldIdentifier: record.master.uld_identifier,
                requestData: values,
                currentTags: currentTags
            });

            const response = await uldService.manageSingleUldTags(record.master.id, values);
            
            console.log('🏷️ 标签管理API响应:', response);
            
            if (response.data?.success) {
                message.success(response.data.message);
                
                console.log('🏷️ 标签管理成功，准备刷新列表...');
                console.log('🏷️ onSuccess函数:', typeof onSuccess);
                
                if (onSuccess) {
                    console.log('🏷️ 调用onSuccess回调...');
                    onSuccess();
                    console.log('🏷️ onSuccess回调已执行');
                } else {
                    console.warn('🏷️ onSuccess回调不存在');
                }
                
                onCancel();
            } else {
                console.error('🏷️ 标签管理失败:', response.data);
                message.error(response.data?.message || "标签管理失败");
            }
        } catch (error) {
            if (error.errorFields) {
                // 表单验证错误
                return;
            }
            console.error('🏷️ 标签管理异常:', error);
            message.error(error.response?.data?.detail || "标签管理失败");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 获取可添加的标签（排除已有的）
    const availableTagsToAdd = tagList.filter(tag => !currentTagNames.includes(tag.tag_name));

    return (
        <Modal
            title={`管理ULD标签 - ${record?.master?.uld_identifier}`}
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={loading}
            destroyOnHidden
            width={700}
        >
            <Form
                form={form}
                layout="vertical"
                preserve={false}
            >
                {/* 当前标签展示 */}
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8, fontWeight: 'bold' }}>当前标签：</div>
                    <Space size={[0, 8]} wrap>
                        {currentTags.length > 0 ? (
                            currentTags.map((tag) => (
                                <Tag 
                                    key={tag.id} 
                                    color={tag.color_code || "#2db7f5"}
                                    style={{ margin: '2px' }}
                                >
                                    {tag.tag_name}
                                </Tag>
                            ))
                        ) : (
                            <span style={{ color: '#999' }}>暂无标签</span>
                        )}
                    </Space>
                </div>

                <Divider />

                {/* 添加标签 */}
                <Form.Item
                    name="tags_to_add"
                    label="添加标签"
                    tooltip="选择要添加的标签"
                >
                    <Select
                        mode="multiple"
                        placeholder="请选择要添加的标签"
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                                                            (option?.children ?? "").toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        disabled={availableTagsToAdd.length === 0}
                    >
                        {availableTagsToAdd.map(tag => (
                            <Option key={tag.id} value={tag.tag_name}>
                                <Space>
                                    <Tag 
                                        color={tag.color_code || "#2db7f5"} 
                                        style={{ margin: 0, minWidth: 60, textAlign: 'center' }}
                                    >
                                        {tag.tag_name}
                                    </Tag>
                                    {tag.description && (
                                        <span style={{ color: '#666' }}>
                                            {tag.description}
                                        </span>
                                    )}
                                </Space>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* 移除标签 */}
                <Form.Item
                    name="tags_to_remove"
                    label="移除标签"
                    tooltip="选择要移除的标签"
                >
                    <Select
                        mode="multiple"
                        placeholder="请选择要移除的标签"
                        allowClear
                        disabled={currentTags.length === 0}
                    >
                        {currentTags.map(tag => (
                            <Option key={tag.id} value={tag.tag_name}>
                                <Space>
                                    <Tag 
                                        color={tag.color_code || "#2db7f5"} 
                                        style={{ margin: 0, minWidth: 60, textAlign: 'center' }}
                                    >
                                        {tag.tag_name}
                                    </Tag>
                                    {tag.description && (
                                        <span style={{ color: '#666' }}>
                                            {tag.description}
                                        </span>
                                    )}
                                </Space>
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* 备注 */}
                <Form.Item
                    name="remarks"
                    label="备注信息"
                >
                    <Input.TextArea 
                        placeholder="请输入标签变更备注（可选）" 
                        rows={3}
                        maxLength={200}
                    />
                </Form.Item>

                {/* 提示信息 */}
                {availableTagsToAdd.length === 0 && currentTags.length === 0 && (
                    <div style={{ 
                        padding: 16, 
                        background: '#f9f9f9', 
                        borderRadius: 4, 
                        textAlign: 'center',
                        color: '#666'
                    }}>
                        暂无可用标签，请先创建标签定义
                    </div>
                )}
            </Form>
        </Modal>
    );
};

export default UldTagManageModal; 